import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@/components/ui/Toast';
import { reviewService } from '@/services/review.service';
import type { Review, AdminResponseFormData } from '@/types/review.types';
import StarRating from '../StarRating';

interface AdminResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: Review | null;
  onSuccess?: () => void;
}

export default function AdminResponseModal({
  isOpen,
  onClose,
  review,
  onSuccess,
}: AdminResponseModalProps) {
  const addResponse = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { response_text: string } }) =>
      reviewService.addAdminResponse(id, data),
    onSuccess: () => {
      toast.success('Response added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add response');
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AdminResponseFormData>({
    defaultValues: {
      response_text: review?.admin_response?.text || '',
    },
  });

  const responseText = watch('response_text');

  // Reset form when modal opens with a review
  useEffect(() => {
    if (isOpen && review) {
      reset({
        response_text: review.admin_response?.text || '',
      });
    }
  }, [isOpen, review, reset]);

  const onSubmit = async (data: AdminResponseFormData) => {
    if (!review) return;

    try {
      await addResponse.mutateAsync({
        id: review._id,
        data: { response_text: data.response_text },
      });
      reset();
      onSuccess?.();
      onClose();
    } catch {
      // Error handled by mutation hook
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!review) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={handleClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <MessageSquare size={20} className="text-primary-600" />
                  <h3 className="font-semibold text-gray-900">
                    {review.admin_response ? 'Edit Response' : 'Add Response'}
                  </h3>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Review Summary */}
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary-700">
                      {review.user_details?.initials ||
                        `${review.user_details?.first_name?.charAt(0) || '?'}${review.user_details?.last_name?.charAt(0) || '?'}`}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {review.user_details?.first_name || 'Unknown'} {review.user_details?.last_name || 'User'}
                    </p>
                    <StarRating value={review.rating} readonly size="sm" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 line-clamp-3">{review.review_text}</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="px-5 py-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Response
                  </label>
                  <Controller
                    name="response_text"
                    control={control}
                    rules={{
                      required: 'Response is required',
                      minLength: {
                        value: 10,
                        message: 'Response must be at least 10 characters',
                      },
                      maxLength: {
                        value: 500,
                        message: 'Response cannot exceed 500 characters',
                      },
                    }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        placeholder="Write your response to this review..."
                        rows={4}
                        className={`w-full px-4 py-3 text-sm border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none ${
                          errors.response_text ? 'border-red-500' : 'border-gray-200'
                        }`}
                        maxLength={500}
                      />
                    )}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.response_text ? (
                      <p className="text-red-500 text-xs">{errors.response_text.message}</p>
                    ) : (
                      <span className="text-xs text-gray-400">Min 10 characters</span>
                    )}
                    <span className="text-xs text-gray-400">{responseText?.length || 0}/500</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Response'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
